import React from "react";
import { renderToStaticMarkup } from 'react-dom/server';

import { env } from "@brz-env";
import getAlt from '@brz-shared/img/alt';
import { usePage } from "@brz-utils/contexts/page.context";
import { useSiteSettings } from "@brz-utils/use.site.settings";
import { toTinaMarkdown } from "@brz-utils/toTinaMarkdown";

import { TinaMarkdown } from 'tinacms/dist/rich-text';
import { RichCurrentYear } from "@brz-components/elements/rich.current.year";


const TESTING = false;


type PropsType = {
    pageType?: 'article' | 'profile' | 'website';
    cover?: string;
    thumbnail?: string;
    author?: {
        name: string;
        settings: { seo: { url: string } }
    } | null;
    publishedDate?: string;
    updatedDate?: string;
};

export const OpenGraphSchema = (props: PropsType) => {
    const site = useSiteSettings();
    const { page, template } = usePage();

    const title = renderToStaticMarkup(<TinaMarkdown 
        content={toTinaMarkdown(page.seo.title)}  
        components={{
            CurrentYear: RichCurrentYear
        }}
    />)

    const metas: Array<{ property?: string; name?: string; content: string; }> = [
        {
            property: `og:site_name`,
            content: site.name
        }, {
            property: `og:title`,
            content: title
        }, {
            property: `og:description`,
            content: page.seo.description || ''
        }, {
            property: `og:locale`,
            content: `en_US`
        }, {
            property: `og:url`,
            content: `${TESTING ? 'https://brz-web-tina-cms-qa.azurewebsites.net/' : env.DEPLOYED_HOST}${page.seo.url}`
        },
    ];

    // Twitter Meta
    metas.push(
        {
            name: `twitter:card`,
            content: `summary_large_image`
        },
        {
            name: `twitter:creator`,
            content: site.twitterAuthor
        },
        {
            name: 'twitter:site',
            content: site.twitterAuthor
        },
        {
            name: `twitter:title`,
            content: title
        },
        {
            name: `twitter:description`,
            content: page.seo.description || ''
        }
    );

    if (props.cover || props.thumbnail || page.seo.image || site.image) {
        metas.push({
            property: `og:image`,
            content: props.cover || props.thumbnail || page.seo.image || site.image
        }, {
            property: `og:image:alt`,
            content: getAlt( props.cover || props.thumbnail || page.seo.image || site.image )
        }, {
            property: `twitter:image`,
            content: props.cover || props.thumbnail || page.seo.image || site.image
        }, {
            property: `twitter:image:alt`,
            content: getAlt( props.cover || props.thumbnail || page.seo.image || site.image )
        });
    }

    if( site.fbAppId ) {
        metas.push({
            property: `fb:app_id`,
            content: site.fbAppId
        });
    }


    metas.push({
        property: `og:type`,
        content: props.pageType || 'website'
    });


    /**
     * If Article
     */
    if ( props.pageType === 'article' && props.author ) {
        metas.push({
            property: 'article:author',
            content: props.author.name
        }, {
            property: 'author',
            content: props.author.name
        });

        if ( props.publishedDate ) {
            metas.push({
                property: 'article:published_time',
                content: props.publishedDate
            }, {
                property: 'published_time',
                content: props.publishedDate
            });
        }
    }


    /**
     * If Author / Profile
     */
    if ( props.pageType === 'profile' && props.author ) {
        metas.push({
            property: 'profile:first_name',
            content: props.author.name.split(' ')?.[0]
        }, {
            property: 'profile:last_name',
            content: props.author.name.split(' ')?.[0]
        });
    }


    return metas.map((meta, index) => {
        return <meta key={index} {...meta} />
    })
};